import {
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    makeStyles,
} from "@material-ui/core";
import * as React from "react";
import { Prompt } from "react-router";

import { Button, DialogCloseIconButton, RelodeTheme } from "..";
import { NotificationIcon } from "../notifications/notificationIcon";
import { Text } from "../text";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                top: -256,
            },
            title: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            },
            titleIcon: {
                display: "flex",
                flexDirection: "row",
                alignItems: "start",
                justifyContent: "space-between",
            },
            titleSpacer: {
                width: theme.spacing(6),
            },
            content: {
                padding: "8px 24px 40px 24px",
            },
            icon: {
                fontSize: 56,
                marginTop: 16,
                marginBottom: theme.spacing(2),
            },
            actionButton: {
                marginRight: theme.spacing(2),
                marginBottom: theme.spacing(2),
            },
        }),
    { name: "ConfirmationPrompt" }
);

export const ConfirmationPrompt = (props: {
    when: boolean;
    navigate: (p: string, saveBefore: boolean) => void;
    shouldBlock: (l: Location) => boolean;
    canSave?: boolean;
}) => {
    const classes = useStyles({});
    const [state, setState] = React.useState({
        modalVisible: false,
        lastLocation: null,
    });
    const confirmed = React.useRef(false);
    React.useEffect(() => {
        if (props.when) {
            confirmed.current = false;
        }
    }, [props.when]);

    React.useEffect(() => {
        const fn = (e: BeforeUnloadEvent) => {
            // this message doesn't appear to be used in all browsers.
            // Chrome and Firefox each have their own messages.
            if (!props.when) return;
            const shouldContinue = confirm(
                "You have unsaved changes. If you continue your changes will be lost."
            );
            if (!shouldContinue) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", fn);
        return () => {
            window.removeEventListener("beforeunload", fn);
        };
    }, [props.when]);

    const showModal = (location: Location) => {
        setState(old => ({
            modalVisible: true,
            lastLocation: location,
        }));
    };

    const closeModal = () => {
        setState(old => ({ ...old, modalVisible: false }));
    };

    const handleBlockedNavigation = (nextLocation: any) => {
        if (!confirmed.current) {
            const should = props.shouldBlock(nextLocation);
            if (!should) return true;
            showModal(nextLocation);
            return false;
        }
        return true;
    };

    const handleConfirmationClick = (shouldSave: boolean) => {
        if (state.lastLocation) {
            setState(old => ({ ...old, modalVisible: false }));
            confirmed.current = true;
            props.navigate(
                state.lastLocation.pathname + state.lastLocation.search,
                shouldSave
            );
        }
    };
    const message = props.canSave
        ? "You have unsaved changes. Choose an option below to continue or close this to cancel the operation."
        : "You have unsaved changes. You may discard your changes to continue or close this to cancel the operation.";
    return (
        <>
            <Prompt when={props.when} message={handleBlockedNavigation} />
            <Dialog
                open={state.modalVisible}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: classes.root }}
                disableEscapeKeyDown={false}
                onEscapeKeyDown={closeModal}
            >
                <DialogTitle disableTypography={true} className={classes.title}>
                    <div className={classes.titleIcon}>
                        <div className={classes.titleSpacer}></div>
                        <NotificationIcon
                            level="warning"
                            size="large"
                            className={classes.icon}
                        />
                        <DialogCloseIconButton onClick={closeModal} />
                    </div>
                    <Text variant="h2" align="center">
                        unsaved changes
                    </Text>
                </DialogTitle>
                <DialogContent className={classes.content}>
                    <Text variant="body1">{message}</Text>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                        className={classes.actionButton}
                        onClick={() => handleConfirmationClick(false)}
                    >
                        discard &amp; continue
                    </Button>
                    {props.canSave ? (
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            className={classes.actionButton}
                            onClick={() => handleConfirmationClick(true)}
                        >
                            save &amp; continue
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>
        </>
    );
};
